/**
 * FolderTree — left pane of the three-pane layout.
 *
 * Features:
 *  - Permanent "All Test Cases" root node (sets selectedFolderId = null).
 *  - View toggle: Tree (collapsible nested) vs. Flat list (all folders sortable).
 *  - Keyboard navigation via ArrowUp/Down/Enter.
 *  - Each node is a drop target for test case drag-and-drop.
 */

import { Loader2, AlertCircle, FolderPlus, List, GitBranch, Layers } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { useFolderTree } from './useFolderTree';
import { FolderTreeNode } from './FolderTreeNode';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import type { ITestFolder } from '../types';

interface FolderTreeProps {
    projectId:       string | null;
    /** Force the pane to be visible regardless of screen size (used in mobile drawer). */
    forceVisible?:   boolean;
    /** Map of folderId → test case count, derived from the loaded test cases list. */
    folderCountMap?: Map<string, number>;
}

/** Flat list item: just a folder node rendered as a clickable row (no children shown). */
function FlatFolderRow({ folder, isSelected, onClick }: {
    folder: ITestFolder;
    isSelected: boolean;
    onClick: () => void;
}) {
    const depth = folder.path.split('/').length - 2; // "/root" = 0, "/root/A" = 1, etc.
    const displayPath = folder.path === '/root'
        ? ''
        : folder.path.replace(/^\/root\//, '').replace(/\//g, ' › ').replace(/ › [^›]+$/, '');

    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full flex flex-col items-start px-3 py-1.5 rounded-lg text-left transition-colors
                ${isSelected
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                }`}
            style={{ paddingLeft: `${8 + depth * 8}px` }}
        >
            <span className="text-xs font-medium truncate w-full">{folder.name}</span>
            {displayPath && (
                <span className="text-[10px] text-slate-400 truncate w-full">{displayPath}</span>
            )}
        </button>
    );
}

export function FolderTree({ projectId, forceVisible = false, folderCountMap }: FolderTreeProps) {
    const {
        folders,
        tree,
        isLoading,
        isError,
        createFolder,
        renameFolder,
        deleteFolder,
    } = useFolderTree(projectId);

    const selectedFolderId  = useTestCasesStore((s) => s.selectedFolderId);
    const setSelectedFolder = useTestCasesStore((s) => s.setSelectedFolderId);
    const expandAll         = useTestCasesStore((s) => s.expandAll);
    const collapseAll       = useTestCasesStore((s) => s.collapseAll);

    const [viewMode, setViewMode] = useState<'tree' | 'flat'>('tree');
    const [creating, setCreating] = useState(false);
    const [newName,  setNewName]  = useState('');

    const treeBodyRef = useRef<HTMLDivElement>(null);

    // ── Keyboard navigation ────────────────────────────────────────────────
    const handleTreeKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!treeBodyRef.current) return;
        const items = Array.from(
            treeBodyRef.current.querySelectorAll<HTMLElement>('[role="treeitem"], button[data-nav]'),
        );
        if (items.length === 0) return;

        const focused = document.activeElement as HTMLElement;
        const idx     = items.indexOf(focused);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = items[idx + 1] ?? items[0];
            next.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = items[idx - 1] ?? items[items.length - 1];
            prev.focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            focused?.click();
        }
    }, []);

    async function handleCreateRoot() {
        if (!newName.trim()) return;
        await createFolder(undefined, newName.trim());
        setNewName('');
        setCreating(false);
    }

    const visibilityClass = forceVisible ? 'flex' : 'hidden md:flex';
    // In a mobile drawer the parent controls width; drop the border & fixed width
    const sizeClass = forceVisible ? 'w-full border-r-0' : 'w-60 border-r border-slate-200 dark:border-gh-border-dark flex-shrink-0';

    if (!projectId) {
        return (
            <aside data-testid="folder-tree" className={`${visibilityClass} ${sizeClass} flex-col h-full`}>
                <div className="flex items-center justify-center flex-1 text-xs text-slate-400 px-4 text-center">
                    Select a project to view folders
                </div>
            </aside>
        );
    }

    return (
        <aside data-testid="folder-tree" className={`${visibilityClass} ${sizeClass} flex-col h-full overflow-hidden`}>
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-gh-border-dark flex-shrink-0">
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Folders
                </span>
                <div className="flex items-center gap-0.5">
                    {/* View mode toggle */}
                    <button
                        data-testid="folder-tree-view-toggle"
                        aria-label={viewMode === 'tree' ? 'Switch to flat list' : 'Switch to tree view'}
                        onClick={() => setViewMode(viewMode === 'tree' ? 'flat' : 'tree')}
                        title={viewMode === 'tree' ? 'Switch to flat list' : 'Switch to tree view'}
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors"
                    >
                        {viewMode === 'tree' ? <List size={13} /> : <GitBranch size={13} />}
                    </button>
                    {/* New folder */}
                    <button
                        data-testid="folder-tree-new-folder-button"
                        aria-label="New folder"
                        onClick={() => { setCreating(true); setNewName(''); }}
                        title="New folder"
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors"
                    >
                        <FolderPlus size={14} />
                    </button>
                </div>
            </div>

            {/* Quick-create root folder */}
            {creating && (
                <div className="px-2 pt-2 pb-1 flex-shrink-0">
                    <input
                        autoFocus
                        aria-label="New folder name"
                        data-testid="folder-tree-new-folder-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter')  handleCreateRoot();
                            if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                        }}
                        placeholder="New folder name"
                        className="w-full px-2 py-1.5 text-xs border border-blue-400 rounded-lg bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 focus:outline-none"
                    />
                    <div className="flex gap-1 mt-1">
                        <button
                            data-testid="folder-tree-create-button"
                            onClick={handleCreateRoot}
                            disabled={!newName.trim()}
                            className="flex-1 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 transition-colors"
                        >
                            Create
                        </button>
                        <button
                            data-testid="folder-tree-cancel-button"
                            onClick={() => { setCreating(false); setNewName(''); }}
                            className="py-1 px-2 text-xs text-slate-500 hover:text-slate-700 rounded-lg"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Tree body */}
            <div
                ref={treeBodyRef}
                role="tree"
                aria-label="Test case folders"
                className="flex-1 overflow-y-auto py-1 px-1"
                onKeyDown={handleTreeKeyDown}
            >
                {/* ── Permanent "All Test Cases" root node ── */}
                <button
                    type="button"
                    data-testid="folder-tree-all-button"
                    data-nav="true"
                    onClick={() => setSelectedFolder(null)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors mb-0.5
                        ${selectedFolderId === null
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                        }`}
                >
                    <Layers size={13} className="flex-shrink-0" />
                    All Test Cases
                </button>

                {/* Divider */}
                {(folders.length > 0 || isLoading) && (
                    <div className="h-px bg-slate-100 dark:bg-slate-800 mx-2 mb-1" />
                )}

                {/* Loading / error / empty */}
                {isLoading && (
                    <div className="flex items-center justify-center py-6">
                        <Loader2 size={16} className="animate-spin text-slate-400" />
                    </div>
                )}
                {isError && (
                    <div className="flex items-center gap-2 px-3 py-3 text-xs text-rose-500">
                        <AlertCircle size={13} /> Failed to load folders
                    </div>
                )}
                {!isLoading && !isError && folders.length === 0 && (
                    <p className="text-xs text-slate-400 px-3 py-4 text-center">
                        No folders yet.<br />Click + to create one.
                    </p>
                )}

                {/* ── Tree view ── */}
                {!isLoading && viewMode === 'tree' && tree.map((node) => (
                    <FolderTreeNode
                        key={node._id}
                        node={node}
                        allFolders={folders}
                        folderCountMap={folderCountMap}
                        onCreateSubfolder={createFolder}
                        onRename={renameFolder}
                        onDelete={deleteFolder}
                    />
                ))}

                {/* ── Flat list view ── */}
                {!isLoading && viewMode === 'flat' && (
                    <div className="flex flex-col gap-0.5">
                        {[...folders]
                            .sort((a, b) => a.path.localeCompare(b.path))
                            .map((folder) => (
                                <FlatFolderRow
                                    key={folder._id}
                                    folder={folder}
                                    isSelected={selectedFolderId === folder._id}
                                    onClick={() => setSelectedFolder(folder._id)}
                                />
                            ))
                        }
                    </div>
                )}
            </div>
        </aside>
    );
}
